import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, UseMutationResult } from '@tanstack/react-query';

import { isAmbiguousFailure } from '../../../../api';
import { BROWSER_PUSH } from '../../push/browser';
import { syncThisDevice, turnOffThisDevice, turnOnThisDevice } from '../../push/device';
import type { DeviceNotifications, PushPermission } from '../../push/device';
import { ctlMutationKeys, ctlMutationScopes } from '../../utils/query-keys';
import { mySettingsQueryOptions } from '../../utils/queryOptions';

import { pushDeviceQueryOptions } from './pushDevice';

const syncBrowser = (): Promise<DeviceNotifications> => syncThisDevice(BROWSER_PUSH);

const turnOnBrowser = (asking: Promise<PushPermission>): Promise<DeviceNotifications> => turnOnThisDevice(BROWSER_PUSH, asking);

const turnOffBrowser = (): Promise<DeviceNotifications> => turnOffThisDevice(BROWSER_PUSH);

const writeDevice = async (queryClient: QueryClient, state: DeviceNotifications): Promise<void> => {
  const { queryKey } = pushDeviceQueryOptions();
  await queryClient.cancelQueries({ queryKey });
  queryClient.setQueryData(queryKey, state);
};

const settleDevice = async (queryClient: QueryClient, state: DeviceNotifications): Promise<void> => {
  await writeDevice(queryClient, state);
  void queryClient.invalidateQueries({ queryKey: mySettingsQueryOptions().queryKey });
};

const rereadDevice = (queryClient: QueryClient, error: Error, withSettings: boolean): void => {
  if (!isAmbiguousFailure(error)) return;
  void queryClient.invalidateQueries({ queryKey: pushDeviceQueryOptions().queryKey });
  if (withSettings) void queryClient.invalidateQueries({ queryKey: mySettingsQueryOptions().queryKey });
};

export const useSyncThisDevice = (): UseMutationResult<DeviceNotifications, Error, void> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ctlMutationKeys.pushSync(),
    scope: ctlMutationScopes.pushDevice,
    mutationFn: syncBrowser,
    onSuccess: (state) => writeDevice(queryClient, state),
    onError: (error) => rereadDevice(queryClient, error, false),
  });
};

export const useTurnOnThisDevice = (): UseMutationResult<DeviceNotifications, Error, Promise<PushPermission>> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ctlMutationKeys.pushSubscribe(),
    scope: ctlMutationScopes.pushDevice,
    mutationFn: turnOnBrowser,
    onSuccess: (state) => settleDevice(queryClient, state),
    onError: (error) => rereadDevice(queryClient, error, true),
  });
};

export const useTurnOffThisDevice = (): UseMutationResult<DeviceNotifications, Error, void> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ctlMutationKeys.pushUnsubscribe(),
    scope: ctlMutationScopes.pushDevice,
    mutationFn: turnOffBrowser,
    onSuccess: (state) => settleDevice(queryClient, state),
    onError: (error) => rereadDevice(queryClient, error, true),
  });
};
